<template>
  <div v-if="show" @click="$emit('close')"
    class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <div @click.stop class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-red-200 dark:border-red-800">
      <div class="flex items-center gap-3 mb-4">
        <div class="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-950 flex items-center justify-center shrink-0">
          <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
          </svg>
        </div>
        <div>
          <h3 class="text-lg font-bold text-slate-900 dark:text-white">Deactivate Account?</h3>
          <p class="text-xs text-slate-500 dark:text-slate-400">This is permanent and irreversible</p>
        </div>
      </div>

      <div class="mb-6">
        <p class="text-sm text-slate-700 dark:text-slate-300 mb-3">
          Deactivating your account will:
        </p>
        <ul class="text-sm text-slate-600 dark:text-slate-400 space-y-1.5 ml-4">
          <li class="flex items-center gap-2">
            <span class="w-1.5 h-1.5 rounded-full bg-red-500"></span>
            Permanently disable your login access
          </li>
          <li class="flex items-center gap-2">
            <span class="w-1.5 h-1.5 rounded-full bg-red-500"></span>
            Mark your account as deactivated
          </li>
          <li class="flex items-center gap-2">
            <span class="w-1.5 h-1.5 rounded-full bg-red-500"></span>
            You will not be able to recover this account
          </li>
        </ul>
        <div class="mt-4 p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
          <p class="text-xs text-red-700 dark:text-red-400 font-medium">
            Warning: This action is irreversible. You will be immediately signed out and cannot sign in again.
          </p>
        </div>
      </div>

      <div class="mb-4">
        <label class="text-sm font-medium text-slate-900 dark:text-white block mb-2">
          Reason for deactivation <span class="text-red-500">*</span>
        </label>
        <textarea
          ref="reasonField"
          :value="reason"
          @input="$emit('update:reason', $event.target.value)"
          rows="3"
          placeholder="Please tell us why you're leaving..."
          required
          class="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700
                 text-slate-900 dark:text-white placeholder-slate-400 rounded-xl px-4 py-3 text-sm
                 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition"
        ></textarea>
      </div>

      <div class="mb-4">
        <label class="text-sm font-medium text-slate-900 dark:text-white block mb-2">
          Confirm your password to continue
        </label>
        <div class="relative" :class="{ 'animate-shake': isShaking }">
          <input
            :value="password"
            @input="$emit('update:password', $event.target.value); $emit('update:error', '')"
            :type="showPassword ? 'text' : 'password'"
            placeholder="Enter your password"
            autocomplete="current-password"
            required
            :class="error ? 'border-red-500 dark:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-700 focus:ring-red-500'"
            class="w-full bg-white dark:bg-slate-800 border
                   text-slate-900 dark:text-white placeholder-slate-400 rounded-xl px-4 py-3 pr-10 text-sm
                   focus:outline-none focus:ring-2 focus:border-transparent transition"
          />
          <button type="button" @click="showPassword = !showPassword"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <svg v-if="showPassword" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
            </svg>
            <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          </button>
        </div>
        <p v-if="error" class="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
          </svg>
          {{ error }}
        </p>
      </div>

      <div class="flex gap-3">
        <button @click="$emit('close')" type="button"
          class="flex-1 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-800 dark:text-white font-medium rounded-xl px-4 py-3 text-sm transition">
          Cancel
        </button>
        <button @click="$emit('confirm')" type="button" :disabled="loading || !password || !reason"
          class="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold rounded-xl px-4 py-3 text-sm transition">
          {{ loading ? 'Deactivating...' : 'Yes, Deactivate' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'

const props = defineProps({
  show: Boolean,
  password: String,
  reason: String,
  error: String,
  loading: Boolean,
  shake: Boolean
})

const emit = defineEmits(['close', 'confirm', 'update:password', 'update:reason', 'update:error'])

const showPassword = ref(false)
const isShaking = ref(false)
const reasonField = ref(null)

// Watch for error changes to trigger shake animation
watch(() => props.error, (newError) => {
  if (newError) {
    isShaking.value = true
    setTimeout(() => {
      isShaking.value = false
    }, 500)
  }
})

// Auto-focus on reason field when modal opens
watch(() => props.show, (isShown) => {
  if (isShown) {
    nextTick(() => {
      reasonField.value?.focus()
    })
  }
})
</script>

<style scoped>
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

.animate-shake {
  animation: shake 0.5s;
}
</style>
